import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injector, input, Signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeMedlemListeModell, SpørreskjemaModell, SpørreundersøkelseModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatStep, MatStepper} from "@angular/material/stepper";
import {MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {MatOption, MatSelect, MatSelectTrigger} from "@angular/material/select";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {combineLatest, filter, map, startWith, switchMap} from "rxjs";
import {MatInput} from "@angular/material/input";
import {VelgMedlemmerTabellKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer-tabell/velg-medlemmer-tabell";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {SpørreundersøkelseBesvarelseSkjemaKomponent, SpørsmålsamlingSkjemaType} from "../spørreundersøkelse-besvarelse-skjema/spørreundersøkelse-besvarelse-skjema";
import {erSpørsmålSomKanBesvares} from "felles-ng2/modell/spørreundersøkelseModellhjelpere";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {SpørreundersøkelseInvitasjonSkjemaKomponent} from "../spørreundersøkelse-invitasjon-skjema/spørreundersøkelse-invitasjon-skjema";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {aktiverFormKontroller, skjemaendringerSomObserverbar} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";

const debug = initDebugger('SpørreundersøkelseSkjemaKomponent');

export type SpørreundersøkelseSkjemaType = ReturnType<typeof SpørreundersøkelseSkjemaKomponent.opprettSkjema>;
export type SpørreundersøkelseSkjemaVerdi = SpørreundersøkelseSkjemaType['value'];

/**
 * Skjema for opprettelse/redigering av spørreundersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-skjema',
    templateUrl: 'spørreundersøkelse-skjema.html',
    styleUrl: 'spørreundersøkelse-skjema.scss',
    imports: [
        MatStepper,
        FormsModule,
        MatFormField,
        MatLabel,
        MatStep,
        TranslateModule,
        ReactiveFormsModule,
        MatHint,
        MatSelect,
        MatOption,
        MatInput,
        VelgMedlemmerTabellKomponent,
        MatSelectTrigger,
        HtmlFormatertKomponent,
        TipsKomponent,
        SpørreundersøkelseBesvarelseSkjemaKomponent,
        SpørreundersøkelseInvitasjonSkjemaKomponent,
        MatSlideToggle,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseSkjemaKomponent {

    static opprettSkjema(destroyRef: DestroyRef) {
        const skjema = new FormGroup({
            skjema: new FormGroup({
                skjema: new FormControl<SpørreskjemaModell | null>(null, {nonNullable: true, validators: Validators.required}),
                tittel: new FormControl('', {nonNullable: true, validators: Validators.required}),
            }),
            respondenter: VelgMedlemmerTabellKomponent.opprettSkjema([]),
            aapneForBesvarelse: new FormControl(true, {nonNullable: true}),
            invitasjon: SpørreundersøkelseInvitasjonSkjemaKomponent.opprettSkjema(),
            eksisterendeUndersoekelse: new FormControl<SpørreundersøkelseModell | null>(null)
        });
        skjema.controls.aapneForBesvarelse.valueChanges
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe(åpen => aktiverFormKontroller(åpen, skjema.controls.invitasjon));
        return skjema;
    }

    static async repopulerSkjema(skjema: SpørreundersøkelseSkjemaType, undersøkelse: SpørreundersøkelseModell, medlemmer: Promise<GruppeMedlemListeModell[]>): Promise<void> {
        skjema.patchValue({
            skjema: {
                tittel: undersøkelse.tittel,
                skjema: undersøkelse.skjema
            },
            invitasjon: {varighet: undersøkelse.responsperiodeAntallDager!},
            eksisterendeUndersoekelse: undersøkelse
        });

        (await medlemmer).forEach(medlem =>
          skjema.controls.respondenter.push(VelgMedlemmerTabellKomponent.opprettSkjemarad(
            medlem,
            undersøkelse.respondenter.some(respondent => respondent.id === medlem.id))));
    }


    protected matStepper = viewChild.required(MatStepper);

    skjemakontroll = input.required<SpørreundersøkelseSkjemaType>();
    tilgjengeligeSkjema = input.required<SpørreskjemaModell[]>();

    erFoersteSteg: Signal<boolean>;
    erSisteSteg: Signal<boolean>;
    erSkjemaForAktivtStegGyldig: Signal<boolean>;

    protected valgtSkjema: Signal<SpørreskjemaModell | null>;
    protected valgtSkjemaSomUndersoekelse: Signal<SpørsmålsamlingSkjemaType | null>;
    protected valgtSkjemaAntallSpoersmaal: Signal<number>;
    public antallRespondenter: Signal<number>; //public for testbarhet
    public antallNyeRespondenter: Signal<number>; //public for testbarhet
    public antallSlettedeRespondenter: Signal<number>; //public for testbarhet

    constructor(injector: Injector) {
        const stegIndeks = toObservable(this.matStepper).pipe(switchMap(stepper => stepper.selectedIndexChange));
        this.erFoersteSteg = toSignal(stegIndeks.pipe(map(indeks => indeks === 0)), {initialValue: true});
        this.erSisteSteg = toSignal(stegIndeks.pipe(map(indeks => indeks === this.matStepper().steps.length - 1)), {initialValue: false});
        this.erSkjemaForAktivtStegGyldig = toSignal(skjemaendringerSomObserverbar(this.skjemakontroll).pipe(
          switchMap(skjemaverdier => toObservable(this.matStepper, {injector: injector})),
          //matStepper.selected er ikke nødvendigvis initialisert initielt
          filter(matStepper => erIkkeNil(matStepper.selected?.stepControl)),
          map(matStepper => matStepper.selected!.stepControl.valid)), {initialValue: true});

        this.valgtSkjema = toSignal(toObservable(this.skjemakontroll).pipe(
          switchMap(skjema => skjema.controls.skjema.valueChanges.pipe(startWith(skjema.value.skjema))),
          map(valgtSkjema => valgtSkjema?.skjema ?? null)), {initialValue: null});
        this.valgtSkjemaSomUndersoekelse = computed(() => this.spørreskjemaSomSpørsmålsamlingSkjema(this.valgtSkjema()));
        this.valgtSkjemaAntallSpoersmaal = computed(() => (this.valgtSkjema()?.spoersmaal ?? [])
          .filter(spørsmål => erSpørsmålSomKanBesvares(spørsmål))
          .length);

        this.antallRespondenter = toSignal(skjemaendringerSomObserverbar(this.skjemakontroll).pipe(
          map(skjemaverdier => skjemaverdier.respondenter!.filter(respondent => respondent.valgt).length)), {initialValue: 0});

        const endringIRespondenter = toObservable(this.skjemakontroll).pipe(
          switchMap(skjema => combineLatest([
              skjema.controls.respondenter.valueChanges.pipe(startWith(skjema.value.respondenter)),
              skjema.controls.eksisterendeUndersoekelse.valueChanges.pipe(startWith(skjema.value.eksisterendeUndersoekelse), map(undersøkelse => undersøkelse?.respondenter ?? [])),
          ])));
        this.antallNyeRespondenter = toSignal(endringIRespondenter.pipe(map(([respondenter, initieltValgteRespondenter]) =>
          this.beregnAntallNyeRespondenter(respondenter, initieltValgteRespondenter))), {initialValue: 0});
        this.antallSlettedeRespondenter = toSignal(endringIRespondenter.pipe(map(([respondenter, initieltValgteRespondenter]) =>
          this.beregnAntallSlettedeRespondenter(respondenter, initieltValgteRespondenter))), {initialValue: 0});
    }

    protected erValgtSkjema(a: SpørreskjemaModell | null, b: SpørreskjemaModell | null) {
        if (a === b) return true;
        if (erNil(a) || erNil(b)) return false;
        return a.id === b.id;
    }

    nesteSteg() {
        this.matStepper().next();
    }

    forrigeSteg() {
        this.matStepper().previous();
    }

    // private hjelpemetoder

    private beregnAntallNyeRespondenter(respondenter: SpørreundersøkelseSkjemaVerdi['respondenter'], initielleRespondenter: GruppeMedlemListeModell[]): number {
        return respondenter!
          .filter(respondent => respondent.valgt)
          .map(respondent => respondent.medlem!)
          .filter(valgtMedlem => !initielleRespondenter.some(initiellRespondent => valgtMedlem.id == initiellRespondent.id))
          .length
    }

    private beregnAntallSlettedeRespondenter(respondenter: SpørreundersøkelseSkjemaVerdi['respondenter'], initielleRespondenter: GruppeMedlemListeModell[]): number {
        return initielleRespondenter
          .filter(initiellRespondent => !respondenter!.some(respondent => respondent.valgt && respondent.medlem!.id == initiellRespondent.id))
          .length
    }

    private spørreskjemaSomSpørsmålsamlingSkjema(skjema: SpørreskjemaModell|null): SpørsmålsamlingSkjemaType | null {
        if (erNil(skjema)) return null;
        return SpørreundersøkelseBesvarelseSkjemaKomponent.opprettIkkeRedigerbartSkjema(skjema);
    }
}
